import axios from 'axios';

// API base URL: use env override if provided, otherwise use relative URLs (same origin)
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  csrf_token?: string;
  token_type: string;
  user_type: string;
  expires_in: number;
  doctor?: {
    id: number;
    email: string;
    is_verified: boolean;
  };
}

export interface GoogleSsoRequest {
  id_token: string;
}

/** Sent as `X-Client-Logout-Reason` on POST /api/v1/auth/logout for server log correlation. */
export const ClientLogoutReason = {
  User: 'user',
  ApiMustRelogin: 'api_must_relogin',
  ModalRelogin: 'modal_relogin',
  OnboardingRefreshFailed: 'onboarding_refresh_failed',
} as const;

const LOGOUT_REASON_HEADER = 'X-Client-Logout-Reason';

function sanitizeClientLogoutReason(raw: string | undefined): string | undefined {
  if (raw == null || typeof raw !== 'string') return undefined;
  const t = raw.trim().slice(0, 120);
  if (!t) return undefined;
  return t.replace(/[^\w.-]/g, '_');
}

class AuthService {
  private token: string | null = null;
  private csrfToken: string | null = null;
  /** Single in-flight refresh so bootstrap + axios interceptor don't race duplicate POST /auth/refresh */
  private refreshInFlight: Promise<string> | null = null;

  // Email/Password login
  async loginWithEmailPassword(email: string, password: string): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(
      apiUrl('/api/v1/auth/login?role=doctor'),
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Auth-Mode': 'cookie',
        },
        withCredentials: true,
      }
    );

    this.token = response.data.access_token;
    this.csrfToken = response.data.csrf_token || null;

    // Tokens are no longer persisted in localStorage.
    // The refresh_token should be returned as an HttpOnly cookie by the backend.

    return response.data;
  }

  // Google SSO login
  async loginWithGoogle(
    idToken: string,
    opts?: { doctorRegistrationAcks?: boolean }
  ): Promise<LoginResponse> {
    const body: Record<string, unknown> = { id_token: idToken };
    if (opts?.doctorRegistrationAcks) {
      body.accept_terms = true;
      body.accept_privacy_policy = true;
      body.accept_dpdp_india_dpa = true;
    }
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/google/verify'),
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Mode': 'cookie',
        },
        withCredentials: true,
      }
    );

    // Extract tokens from nested AuthResponse structure
    const tokens = response.data.tokens;
    this.token = tokens.access_token;
    this.csrfToken = tokens.csrf_token || null;

    return {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      csrf_token: tokens.csrf_token,
      token_type: 'bearer',
      user_type: 'doctor',
      expires_in: tokens.expires_in,
      doctor: response.data.doctor,
    };
  }

  // Doctor registration (Step 1 - Account Creation). Same API as Android/iOS.
  async registerDoctor(
    email: string,
    password: string,
    legal: {
      acceptTerms: boolean;
      acceptPrivacyPolicy: boolean;
      acceptDpdpIndiaDpa: boolean;
    }
  ): Promise<{ message: string; email: string }> {
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/email/register'),
      {
        email,
        password,
        accept_terms: legal.acceptTerms,
        accept_privacy_policy: legal.acceptPrivacyPolicy,
        accept_dpdp_india_dpa: legal.acceptDpdpIndiaDpa,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return {
      message: response.data?.message ?? 'Registration successful. Please check your email to verify your account.',
      email: response.data?.email ?? email,
    };
  }

  // Resend doctor verification email (same as mobile).
  async resendDoctorVerificationEmail(email: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/email/resend-verification'),
      { email },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data?.message ?? 'If an account exists, a verification email has been sent.';
  }

  // Request password reset
  async requestPasswordReset(email: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/auth/forgot-password'),
      { email, realm: 'doctor' },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return (
      response.data?.message ||
      'If this email exists, a password reset link has been sent.'
    );
  }

  // Verify password reset token
  async verifyResetToken(token: string): Promise<{ valid: boolean; message: string }> {
    const response = await axios.get(
      apiUrl(`/api/v1/auth/verify-reset-token/${encodeURIComponent(token)}`)
    );
    return {
      valid: Boolean(response.data?.valid),
      message: response.data?.message ?? '',
    };
  }

  // Complete password reset
  async resetPassword(token: string, newPassword: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/auth/reset-password'),
      { token, new_password: newPassword },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data?.message ?? 'Your password has been reset successfully.';
  }

  // Get stored token
  getToken(): string | null {
    return this.token;
  }

  // Get stored CSRF token
  getCsrfToken(): string | null {
    // If the backend sets a non-HttpOnly CSRF cookie, you can read it here using document.cookie.
    // For now, we return the memory token.
    if (this.csrfToken) {
      return this.csrfToken;
    }
    
    // Example of reading a CSRF cookie:
    const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'));
    if (match) return match[2];
    
    return null;
  }

  // Store tokens
  storeTokens(accessToken: string, refreshToken: string, csrfToken?: string): void {
    this.token = accessToken;
    this.csrfToken = csrfToken || null;
    // We no longer store tokens in localStorage to prevent XSS attacks.
    // The refresh_token should now be handled by the backend as an HttpOnly cookie.
  }

  /**
   * Revokes the session on the server (when `remoteLogout` is true), then clears in-memory tokens.
   * Always await this before navigating away so the logout request can use the current bearer + cookies.
   * Use `{ remoteLogout: false }` when another tab already called logout (BroadcastChannel sync).
   * Optional `reason` is sent as `X-Client-Logout-Reason` when `remoteLogout` is true.
   */
  async clearTokens(options?: { remoteLogout?: boolean; reason?: string }): Promise<void> {
    const remoteLogout = options?.remoteLogout !== false;
    const reason = sanitizeClientLogoutReason(options?.reason);
    if (remoteLogout) {
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Auth-Mode': 'cookie',
        };
        if (reason) {
          headers[LOGOUT_REASON_HEADER] = reason;
        }
        await axios.post(
          apiUrl('/api/v1/auth/logout'),
          {},
          {
            headers,
            withCredentials: true,
            timeout: 5000,
          }
        );
      } catch {
        // Still clear local session so the UI cannot keep using a stale access token.
      }
    }

    try {
      const { unregisterDoctorWebPush } = await import('../firebase/webPush');
      await unregisterDoctorWebPush();
    } catch {
      // ignore (e.g. dynamic import / messaging unsupported)
    }

    if (process.env.NODE_ENV !== 'production') {
      console.warn('[auth] clearTokens called', new Error().stack);
    }
    this.token = null;
    this.csrfToken = null;
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  getRefreshToken(): string | null {
    // The refresh token is now an HttpOnly cookie, so the frontend cannot read it.
    // We return a dummy value so the rest of the app knows it can *attempt* a refresh.
    return 'cookie-managed';
  }

  // Refresh access token using refresh_token
  async refreshAccessToken(): Promise<string> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = (async () => {
      const response = await axios.post(
        apiUrl('/api/v1/auth/refresh'),
        {}, // Backend should now read the refresh_token from the HttpOnly cookie
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Mode': 'cookie',
          },
          withCredentials: true, // Crucial for sending the HttpOnly cookie
        }
      );

      const newAccessToken = response.data.access_token as string;
      const newCsrfToken = response.data.csrf_token as string | undefined;

      this.token = newAccessToken;

      if (newCsrfToken) {
        this.csrfToken = newCsrfToken;
      }

      return newAccessToken;
    })();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  // Get or refresh CSRF token for authenticated users
  async ensureCsrfToken(): Promise<string | null> {
    // If we already have a CSRF token, return it
    const existingToken = this.getCsrfToken();
    if (existingToken) {
      return existingToken;
    }

    // If user is authenticated but CSRF token is missing, refresh the access token
    // which will also return a new CSRF token
    if (this.isAuthenticated()) {
      try {
        await this.refreshAccessToken();
        return this.getCsrfToken();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh CSRF token:', error);
        return null;
      }
    }

    return null;
  }

  // Get auth headers for API calls
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return headers;
  }
}

export const authService = new AuthService();

